// src/components/circles/CircleSetup.jsx
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useCirclesStore } from '../../stores/circlesStore';
import { usePrivacyAuth } from '../../context/PrivacyAuthContext';
import { useApp } from '../../context/AppContext';
import WelcomeToCircle from './WelcomeToCircle';

const EMOJI_OPTIONS = ['🌟', '🔥', '💫', '🌈', '⚡', '💎', '🎯', '🚀', '🌺', '🦋'];

export default function CircleSetup({ userId, onComplete, initialMode = 'choice' }) {
  const { user } = usePrivacyAuth();
  const { setCurrentView } = useApp();
  // Out of set-up without a circle. (onComplete opens the circle dashboard,
  // which sends someone with no circle straight back here.)
  const leaveSetup = () => setCurrentView('home');

  // Check URL for parameters
  const urlParams = new URLSearchParams(window.location.search);
  const joinCode = urlParams.get('join');
  const modeParam = urlParams.get('mode'); // 'create' or 'join' from Welcome page

  // Determine the requested mode: join code > mode param > prop > default.
  // Creating or joining always goes through the age question first - the
  // Welcome page and the sign-up circle prompt link straight to create/join,
  // which used to skip it.
  const determineRequestedMode = () => {
    if (joinCode) return 'join';
    if (modeParam === 'create' || modeParam === 'join') return modeParam;
    return initialMode;
  };
  const requestedMode = determineRequestedMode();
  const needsAgeGate = requestedMode === 'create' || requestedMode === 'join';

  const [mode, setMode] = useState(needsAgeGate ? 'age-gate' : requestedMode);
  const [circleName, setCircleName] = useState('');
  const [selectedEmoji, setSelectedEmoji] = useState('🌟');
  const [inviteCode, setInviteCode] = useState(joinCode || '');
  const [createdCircle, setCreatedCircle] = useState(null);
  const [error, setError] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [nextMode, setNextMode] = useState(needsAgeGate ? requestedMode : null);

  const { createCircle, joinCircle, isLoading, getActiveCircle } = useCirclesStore();

  const handleModeChange = (newMode) => {
    if (!ageConfirmed && (newMode === 'create' || newMode === 'join')) {
      setNextMode(newMode);
      setMode('age-gate');
    } else {
      setMode(newMode);
    }
  };

  // One age floor for the whole app: 13 and over. (This used to say 11-25,
  // against the Privacy Policy's 13.) It is a question, not a check - the
  // rules and the design are what keep circles safe - but it is the same
  // question everywhere. Adults are welcome in the rest of the app; circles
  // are for the young people using it.
  const handleAgeConfirm = (answer) => {
    if (answer === '13-plus') {
      setAgeConfirmed(true);
      setError('');
      setMode(nextMode);
      return;
    }
    setError(answer === 'under-13'
      ? 'Friendships Vibe is for people aged 13 and over, so Friend Circles aren\u2019t open to you yet.'
      : 'Friend Circles are for the young people using the app, so adults can\u2019t create or join one. Everything else in the app is open to you.');
  };

  const handleCreateCircle = async () => {
    if (!circleName.trim()) {
      setError('Please enter a circle name');
      return;
    }

    const username = user?.username || user?.displayName || null;
    const result = await createCircle(userId, circleName, selectedEmoji, username);

    if (result.success) {
      // Store the created circle data
      setCreatedCircle(result.circle);
      // Show welcome modal
      setMode('welcome');
    } else {
      setError(result.error || 'Failed to create circle');
    }
  };

  const handleJoinCircle = async () => {
    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    const username = user?.username || user?.displayName || null;
    const result = await joinCircle(userId, inviteCode, username);

    if (result.success) {
      // There is no pending state: having the code IS the invitation, and
      // joinCircleByCode adds the person straight to members. The store has
      // already made the joined circle the active one.
      onComplete?.();
    } else {
      setError(result.error || 'Failed to join circle');
    }
  };


  if (mode === 'age-gate') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-2xl p-8"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Before you start
            </h2>
            <p className="text-purple-200">
              Friend Circles are for people aged 13 and over, doing the modules with friends they already know.
            </p>
          </div>

          <div className="space-y-3">
            <button
              type="button"
              onClick={() => handleAgeConfirm('13-plus')}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold py-4 px-6 rounded-xl transition-colors"
            >
              I&apos;m 13 or older
            </button>

            <button
              type="button"
              onClick={() => handleAgeConfirm('under-13')}
              className="w-full bg-white/10 hover:bg-white/20 text-white font-medium py-4 px-6 rounded-xl transition-colors"
            >
              I&apos;m under 13
            </button>

            <button
              type="button"
              onClick={() => handleAgeConfirm('adult-exploring')}
              className="w-full bg-white/10 hover:bg-white/20 text-white font-medium py-4 px-6 rounded-xl transition-colors"
            >
              I&apos;m a parent or teacher having a look
            </button>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              role="status"
              className="mt-4 p-4 bg-blue-500/20 border border-blue-400/30 rounded-lg text-blue-200 text-sm text-center"
            >
              {error}
              <button
                type="button"
                onClick={leaveSetup}
                className="mt-3 block w-full text-blue-100 underline underline-offset-4 hover:text-white"
              >
                Back to the app
              </button>
            </motion.div>
          )}
        </motion.div>
      </div>
    );
  }

  if (mode === 'choice') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Friend Circles 🌟
            </h1>
            <p className="text-purple-200">
              Do the modules with friends you already know: the same module, the same question, then talk.
            </p>
          </div>

          <div className="space-y-4">
            <button
              onClick={() => handleModeChange('create')}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105"
            >
              <div className="text-2xl mb-1">👥</div>
              <div>Create a Circle</div>
              <div className="text-sm opacity-75">Start a new squad</div>
            </button>

            <button
              onClick={() => handleModeChange('join')}
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105"
            >
              <div className="text-2xl mb-1">🔗</div>
              <div>Join a Circle</div>
              <div className="text-sm opacity-75">Use an invite code</div>
            </button>

            <button
              onClick={leaveSetup}
              className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-6 rounded-xl transition-all"
            >
              Skip for now
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (mode === 'create') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-2xl p-8"
        >
          <button
            onClick={() => handleModeChange('choice')}
            className="text-purple-200 hover:text-white mb-6 flex items-center gap-2"
          >
            ← Back
          </button>

          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
            Create Your Circle
          </h2>

          <div className="space-y-6">
            <div>
              <label className="block text-purple-200 mb-2 font-medium">
                Circle Name
              </label>
              <input
                type="text"
                value={circleName}
                onChange={(e) => setCircleName(e.target.value)}
                placeholder="e.g., The Squad, Besties, Dream Team"
                className="w-full bg-white/20 border-2 border-purple-300/30 rounded-xl px-4 py-3 text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400"
                maxLength={30}
              />
            </div>

            <div>
              <label className="block text-purple-200 mb-2 font-medium">
                Choose an Emoji
              </label>
              <div className="grid grid-cols-5 gap-3">
                {EMOJI_OPTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => setSelectedEmoji(emoji)}
                    className={`text-3xl p-3 rounded-xl transition-all ${
                      selectedEmoji === emoji
                        ? 'bg-purple-500 scale-110'
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-xl">
                {error}
              </div>
            )}

            <button
              onClick={handleCreateCircle}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-4 px-6 rounded-xl transition-all disabled:opacity-50"
            >
              {isLoading ? 'Creating...' : 'Create Circle'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (mode === 'join') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-2xl p-8"
        >
          <button
            onClick={() => handleModeChange('choice')}
            className="text-purple-200 hover:text-white mb-6 flex items-center gap-2"
          >
            ← Back
          </button>

          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Join a Circle
          </h2>
          <p className="text-purple-200 mb-6">
            Enter the invite code your friend shared
          </p>

          <div className="space-y-6">
            <div>
              <label className="block text-purple-200 mb-2 font-medium">
                Invite Code
              </label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                className="w-full bg-white/20 border-2 border-purple-300/30 rounded-xl px-4 py-3 text-white text-xl sm:text-2xl tracking-wider text-center placeholder-purple-300/50 focus:outline-none focus:border-purple-400"
                maxLength={6}
              />
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-xl">
                {error}
              </div>
            )}

            <button
              onClick={handleJoinCircle}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold py-4 px-6 rounded-xl transition-all disabled:opacity-50"
            >
              {isLoading ? 'Joining...' : 'Join Circle'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Show welcome modal after creating circle
  if (mode === 'welcome' && createdCircle) {
    return (
      <WelcomeToCircle
        circle={createdCircle}
        onContinue={onComplete}
      />
    );
  }

  return null;
}
